module Shared.Dto.Dto

open System
open Shared.Dto.User
open Shared.DtoTypes.PhysicalSensor
open Shared.DtoTypes.LeafletWetness

type SessionKey = SessionKey of string

let sessionKeyToString (SessionKey key) = key

type UserSession = {
    User: LoggedInUserDto
    SessionKey: SessionKey
}

type Session =
    | Anonymous
    | UserSession of UserSession

type LoginRequestData = {
    Mail: string
    Password: String
}

type LoginResponse =
    | Successful of UserSession
    | Unsuccessful of string

type AuthenticatedRequest<'a> = {
    SessionKey: SessionKey
    Data: 'a
}

type AuthenticationError =
    | Unauthorized
    | Unauthenticated

type AuthenticatedResponseError =
    | AuthErr of AuthenticationError
    | CustomErr of string

[<RequireQualifiedAccess>]
module AuthenticatedResponseError =
    let toString (err: AuthenticatedResponseError) =
        match err with
        | AuthErr Unauthorized -> "Du darfst auf diese Daten nicht zugreifen. Bitte wende dich an einen Administrator."
        | AuthErr Unauthenticated -> "Du bist nicht eingeloggt. Bitte lade die Seite neu."
        | CustomErr err -> err

type AuthenticatedResponse<'a> = Result<'a, AuthenticatedResponseError>

let getBaseData sensor =
    match sensor with
    | Configured configured -> configured.BaseData
    | UnConfigured baseData -> baseData

let getUserId sensor = (getBaseData sensor).UserId


let getDeviceEui sensor = (getBaseData sensor).DeviceEui

let getSensorName sensor = (getBaseData sensor).Name

let getSensorDeviceEui sensor = (getBaseData sensor).DeviceEui

let getSensorAppEui sensor = (getBaseData sensor).AppEui

let getSensorAppKey sensor = (getBaseData sensor).AppKey

let getDescription sensor = (getBaseData sensor).TtsDescription

let getSensorModel sensor =
    match sensor with
    | Configured configured -> Some configured.Configuration
    | UnConfigured _ -> None

let private sensorModelToLabel sensorType =
    match sensorType with
    | LHT65 -> "Luftsensor (LHT65)"
    | LSN50v2_S31 -> "Luftsensor (LSN50v2_S31)"
    | S31_LS -> "Luftsensor Solar (S31_LS)"
    | S31_LB -> "Luftsensor Batterie (S31_LB)"
    | LSE01 -> "Bodensensor (LSE01)"
    | SE01_LS -> "Bodensensor Solar (SE01_LS)"
    | SE01_LB -> "Bodensensor Batterie (SE01_LB)"
    | LSN50v2_Rain_01mm -> "Regensensor 0,1mm (LSN50v2)"
    | LSN50v2_Rain_02mm -> "Regensensor 0,2mm (LSN50v2)"
    | SN50v3_LS_Rain_01mm -> "Regensensor 0,1mm Solar (SN50v3_LS)"
    | SN50v3_LS_Rain_02mm -> "Regensensor 0,2mm Solar (SN50v3_LS)"
    | SN50v3_LB_Rain_01mm -> "Regensensor 0,1mm Batterie (SN50v3_LB)"
    | SN50v3_LB_Rain_02mm -> "Regensensor 0,2mm Batterie (SN50v3_LB)"
    | LSN50v2_Wind -> "Wind Durchschnitt (LSN50v2)"
    | SN50v3_LS_Wind -> "Wind Durchschnitt Solar (SN50v2_LS)"
    | SN50v3_LB_Wind -> "Wind Durchschnitt Batterie (SN50v2_LB)"
    | LLMS01 -> "Blattnässe (LLMS01)"
    | LSPH01 -> "PH-Wert (LSPH01)"
    | SPH01_LB -> "PH-Wert Batterie (SPH01-LB)"
    | SPH01_LS -> "PH-Wert Solar (SPH01-LS)"
    | WSC1L -> "Wetterstation (WSC1-L)"
    | DDS20_LS -> "Ultraschall Füllstand Solar (DDS20_LS)"
    | DDS20_LB -> "Ultraschall Füllstand Batterie (DDS20_LB)"
    | RS485_LB_ZFS_02 -> "Globalstrahlung Batterie (RS485_LB ZFS_02)"
    | RS485_LS_ZFS_02 -> "Globalstrahlung Solar (RS485_LS ZFS_02)"
    | SE0X_LB -> "4-fach Bodensensor Batterie (SE0x-LB)"
    | PS_LB(WaterDepth meters) -> sprintf "Drucksensor Batterie (PS_LB): Wassertiefe (%d)" meters.Depth
    | PS_LS(WaterDepth meters) -> sprintf "Drucksensor Solar (PS_LS): Wassertiefe (%d)" meters.Depth
    | PS_LB Pressure -> "Drucksensor Batterie (PS_LB): Druck"
    | PS_LS Pressure -> "Drucksensor Solar (PS_LB): Druck"
    | LDDS75 -> "Distanz Sensor (LDDS75)"
    | DDS75_LB -> "Distanz Sensor Batterie (DDS75_LB)"
    | DDS75_LS -> "Distanz Sensor Solar (DDS75_LS)"
    | WQS_LB -> "Wasser Qualität Sensor Batterie (WQS_LB)"
    | WQS_LS -> "Wasser Qualität Sensor Solar (WQS_LS)"

let sensorModelAsString sensor =
    match sensor with
    | Configured configured -> Some(sensorModelToLabel configured.Configuration)
    | UnConfigured _ -> None


type PasswordResetRequest = {
    RequestedAt: DateTimeOffset
    UserId: int
    Token: string
}

let leafletWetnessToInt wetness =
    match wetness with
    | Wet _ -> 1
    | Dry -> 0

type BatteryLevel =
    | Full
    | TwoQuarters
    | OneQuarter
    | Low
    | Danger
    | Unknown

type LeafletWetnessConfiguration = {
    WetHumidityLimit: float
    DryHumidityLimit: float

    DewPointLimit: float

    RelativeHumidityDeltaLimit: float
    AbsoluteHumidityFactorLimit: float
}

type DateRange = {
    Start: DateTime
    End: DateTime
}


type SensorDataRequest = {
    Range: DateRange
    SensorId: int
}